import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Globe, DownloadSimple, UploadSimple, CheckCircle, WarningCircle, Question,
  ArrowClockwise, X, PencilSimple, ClockCounterClockwise, ArrowRight, CaretLeft, CaretRight, Check, MapPin,
} from "@phosphor-icons/react";
import DomainImportWizard from "@/components/admin/DomainImportWizard";

const STATUS_META = {
  verified: { label: "Vérifié", color: "#22C55E", icon: CheckCircle },
  probable: { label: "Probable", color: "#F5A623", icon: Question },
  ambiguous: { label: "À vérifier", color: "#F97316", icon: WarningCircle },
  missing: { label: "Manquant", color: "#8A8A93", icon: X },
  invalid: { label: "Invalide", color: "#EF4444", icon: X },
};

const fmtNum = (n) => (n ?? 0).toLocaleString("fr-FR");

export default function AdminDomains() {
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: "missing", country: "", region: "", source: "", q: "" });
  const [priority, setPriority] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [conflicts, setConflicts] = useState({ conflicts: [], reviews: [] });
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkValidating, setBulkValidating] = useState(false);
  const [gate, setGate] = useState(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [geoState, setGeoState] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/company-directory/stats"); setStats(data); } catch { /* */ }
  }, []);

  const loadGate = useCallback(async () => {
    try { const { data } = await api.get("/admin/domains/gate"); setGate(data); } catch { /* */ }
  }, []);

  const loadGeo = useCallback(async () => {
    try { const { data } = await api.get("/admin/geo/backfill-state"); setGeoState(data); } catch { /* */ }
  }, []);

  const loadConflicts = useCallback(async () => {
    try { const { data } = await api.get("/admin/company-directory/conflicts?status=open"); setConflicts(data); } catch { /* */ }
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ status: filters.status, country: filters.country, region: filters.region, source: filters.source, q: filters.q, sort: priority ? "priority" : "name", limit: String(pageSize), offset: String(page * pageSize) });
      const { data } = await api.get(`/admin/company-directory/companies?${p}`);
      setCompanies(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters, priority, page, pageSize]);

  useEffect(() => { loadStats(); loadConflicts(); loadGate(); loadGeo(); }, [loadStats, loadConflicts, loadGate, loadGeo]);
  useEffect(() => {
    if (geoState?.state?.status !== "running") return;
    const t = setInterval(loadGeo, 3000);
    return () => clearInterval(t);
  }, [geoState?.state?.status, loadGeo]);
  useEffect(() => { const t = setTimeout(loadCompanies, 250); return () => clearTimeout(t); }, [loadCompanies]);
  // Reset pagination + selection when filters / page size change
  useEffect(() => { setPage(0); setSelectedIds(new Set()); }, [filters, priority, pageSize]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams({ status: filters.status, country: filters.country, region: filters.region, source: filters.source, sort: priority ? "priority" : "name" });
      const res = await api.get(`/admin/company-directory/export?${p}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `interko_societes_sans_domaine_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé — enrichissez-le dans Prospeo, puis réimportez-le.");
      loadStats();
    } catch { toast.error("Export impossible"); } finally { setExporting(false); }
  };

  const saveDomain = async (c) => {
    try {
      await api.patch(`/admin/company-directory/company/${c.id}/domain`, { domain: editVal.trim() });
      toast.success(editVal.trim() ? `Domaine enregistré pour ${c.name}` : "Domaine effacé");
      setEditing(null); setEditVal(""); loadCompanies(); loadStats();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const validateOne = async (c) => {
    if (!c.domain) return;
    try {
      await api.patch(`/admin/company-directory/company/${c.id}/domain`, { domain: c.domain });
      toast.success(`Domaine validé pour ${c.name}`);
      loadCompanies(); loadStats();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const pageAllSelected = companies.length > 0 && companies.every((c) => selectedIds.has(c.id));
  const toggleSelectAll = () => setSelectedIds((s) => {
    const n = new Set(s);
    if (pageAllSelected) companies.forEach((c) => n.delete(c.id));
    else companies.forEach((c) => n.add(c.id));
    return n;
  });

  const bulkValidate = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkValidating(true);
    try {
      const { data } = await api.post("/admin/company-directory/bulk-validate", { ids });
      toast.success(`${fmtNum(data.validated)} domaine(s) validé(s)${data.skipped ? ` · ${fmtNum(data.skipped)} ignoré(s) (sans domaine)` : ""}`);
      setSelectedIds(new Set()); loadCompanies(); loadStats();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkValidating(false); }
  };

  const resolveConflict = async (id, action) => {
    try { await api.post(`/admin/company-directory/conflicts/${id}/resolve`, { action }); loadConflicts(); loadStats(); loadCompanies(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/company-directory/reconcile");
      toast.success(`${fmtNum(data.reconciled)} société(s) reclassée(s) — statuts de domaine réparés`);
      loadStats(); loadCompanies();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReconciling(false); }
  };

  const missingCount = stats?.missing ?? 0;

  const toggleGate = async () => {
    setGateBusy(true);
    try {
      const next = !(gate?.enabled);
      const { data } = await api.post("/admin/domains/gate", { enabled: next });
      setGate(data);
      toast.success(next ? "Gate activé : les entreprises sans site sont masquées" : "Gate désactivé : les entreprises sans site sont affichées");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGateBusy(false); }
  };

  const runGeoBackfill = async () => {
    setGeoBusy(true);
    try { await api.post("/admin/geo/backfill-coords", {}); toast.success("Géocodage lancé en arrière-plan"); setTimeout(loadGeo, 1000); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGeoBusy(false); }
  };

  return (
    <div data-testid="admin-domains-page" className="max-w-[1200px]">
      <div className="flex items-center gap-2 mb-1">
        <Globe size={22} weight="duotone" className="text-accent" />
        <h1 className="font-display text-3xl font-bold">Enrichissement domaines</h1>
      </div>
      <p className="text-muted text-sm mb-6">Le domaine société est l'identifiant clé de la recherche de décideurs. Construisez progressivement l'annuaire InterKO : exportez les sociétés sans domaine, enrichissez-les dans Prospeo, réimportez — InterKO les mémorise définitivement.</p>

      <div className="flex items-center justify-between gap-3 flex-wrap -mt-3 mb-4">
        <p className="text-xs text-muted max-w-2xl">Une société n'apparaît en « manquant » que si elle n'a réellement aucun domaine. Si d'anciennes sociétés ayant déjà un domaine étaient marquées « manquant », cliquez « Réparer les statuts » pour les reclasser.</p>
        <button onClick={reconcile} disabled={reconciling} data-testid="reconcile-domains-btn"
          className="flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent px-3 py-2 rounded-md hover:bg-accent/10 disabled:opacity-60 shrink-0">
          <ArrowClockwise size={14} weight="bold" /> {reconciling ? "Réparation…" : "Réparer les statuts"}
        </button>
      </div>

      {/* Coverage stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="domain-stats">
        <StatCard label="Sociétés" value={fmtNum(stats?.total)} />
        <StatCard label="Couverture" value={`${stats?.coverage_rate ?? 0}%`} accent="#00E5FF" />
        <StatCard label="Vérifiés" value={fmtNum(stats?.verified)} accent="#22C55E" />
        <StatCard label="Probables" value={fmtNum(stats?.probable)} accent="#F5A623" />
        <StatCard label="Manquants" value={fmtNum(stats?.missing)} accent="#8A8A93" />
        <StatCard label="À vérifier" value={fmtNum((stats?.ambiguous ?? 0))} accent="#F97316" />
      </div>

      {/* Maintenance : gate domaine + géocodage des annonces */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="surface rounded-lg p-4" data-testid="domain-gate-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-display font-bold text-fg text-sm">Gate domaine ESN</div>
              <p className="text-xs text-muted mt-1 max-w-sm">Quand il est actif, les entreprises <b>sans site web</b> sont masquées des résultats ESN (la recherche de décideurs nécessite un domaine). Désactivez-le pour afficher aussi ces entreprises (ex : WORLDLINE).</p>
            </div>
            <button onClick={toggleGate} disabled={gateBusy || gate == null} data-testid="domain-gate-toggle"
              role="switch" aria-checked={!!gate?.enabled} title="Activer / désactiver le gate domaine"
              className={`shrink-0 w-12 h-6 rounded-full transition-colors relative ${gate?.enabled ? "bg-accent" : "bg-surface-2 border border-border"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${gate?.enabled ? "left-[26px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="text-xs font-mono mt-3" data-testid="domain-gate-status">
            {gate == null ? "…" : gate.enabled
              ? <span className="text-[#22C55E]">Actif — entreprises sans domaine masquées</span>
              : <span className="text-amber-300">Désactivé — toutes les entreprises affichées</span>}
          </div>
        </div>

        <div className="surface rounded-lg p-4" data-testid="geo-backfill-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-display font-bold text-fg text-sm">Géocodage des annonces</div>
            {geoState?.state?.status && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-accent/30 text-accent bg-accent/10" data-testid="geo-backfill-status">
                {geoState.state.status === "running" ? `En cours… ${fmtNum(geoState.state.processed)}/${fmtNum(geoState.state.total)}` : geoState.state.status}
              </span>
            )}
          </div>
          <p className="text-xs text-muted mt-1 max-w-sm"><b>{fmtNum(geoState?.remaining_ungeocoded)}</b> annonce(s) sans coordonnées. Géocode par nom de commune (référentiel 34 889 communes) pour bien les placer sur la carte et dans les rayons de recherche.</p>
          <button onClick={runGeoBackfill} disabled={geoBusy || geoState?.state?.status === "running" || !geoState?.remaining_ungeocoded} data-testid="geo-backfill-btn"
            className="mt-3 flex items-center gap-1.5 text-xs font-semibold bg-accent text-black px-3 py-2 rounded-md hover-lift disabled:opacity-50">
            <MapPin size={14} weight="fill" /> Géocoder les annonces
          </button>
          {geoState?.state?.status && geoState.state.total != null && (
            <div className="mt-3 space-y-1.5" data-testid="geo-backfill-progress">
              <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${geoState.state.total ? Math.round((geoState.state.processed / geoState.state.total) * 100) : 0}%` }} />
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-muted font-mono">
                <span className="text-[#22C55E]">Géocodées : {fmtNum(geoState.state.fixed)}</span>
                <span>Homonymes : {fmtNum(geoState.state.ambiguous)}</span>
                <span>Non résolues : {fmtNum(geoState.state.unresolved)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Workflow card */}
      <div className="surface rounded-lg p-6 mb-6" data-testid="enrichment-workflow">
        <div className="overline mb-4">Enrichir avec Prospeo</div>
        <div className="grid md:grid-cols-3 gap-4">
          <WorkflowStep n={1} title="Exporter" desc="Exportez les sociétés dont le domaine est encore inconnu.">
            <button onClick={exportCsv} disabled={exporting || missingCount === 0} data-testid="export-missing-btn"
              className="w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold px-4 py-2.5 rounded-md hover-lift disabled:opacity-50">
              <DownloadSimple size={16} weight="bold" />
              {exporting ? "Génération…" : `Exporter les ${fmtNum(missingCount)} sociétés sans domaine`}
            </button>
            <label className="flex items-center gap-2 text-[11px] text-muted mt-2 cursor-pointer" data-testid="priority-toggle-label">
              <input type="checkbox" checked={priority} onChange={(e) => setPriority(e.target.checked)} className="accent-[#00E5FF]" data-testid="priority-toggle" />
              Prioriser les sociétés à forte valeur (signaux actifs, intérêt ESN)
            </label>
          </WorkflowStep>
          <WorkflowStep n={2} title="Enrichir dans Prospeo" desc="Chargez le CSV dans Prospeo et récupérez les domaines / sites web." />
          <WorkflowStep n={3} title="Réimporter" desc="Réimportez le fichier enrichi. InterKO fait le matching et mémorise.">
            <button onClick={() => setShowImport(true)} data-testid="open-import-btn"
              className="w-full flex items-center justify-center gap-2 border border-accent/40 text-accent font-semibold px-4 py-2.5 rounded-md hover:bg-accent/10">
              <UploadSimple size={16} weight="bold" /> Importer un CSV Prospeo
            </button>
          </WorkflowStep>
        </div>
      </div>

      {/* Conflicts + reviews */}
      {(conflicts.conflicts.length > 0 || conflicts.reviews.length > 0) && (
        <div className="surface rounded-lg p-5 mb-6 border border-[#F97316]/30" data-testid="domain-conflicts">
          <div className="flex items-center gap-2 mb-3"><WarningCircle size={16} className="text-[#F97316]" weight="fill" />
            <span className="font-display font-bold text-fg">Correspondances à vérifier ({conflicts.conflicts.length + conflicts.reviews.length})</span></div>
          <div className="space-y-2">
            {conflicts.conflicts.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 bg-surface-2 border border-border rounded-md px-3 py-2 text-sm" data-testid={`conflict-${c.id}`}>
                <div className="min-w-0">
                  <span className="text-fg font-medium">{c.name}</span>
                  <span className="text-muted"> · existant </span><span className="text-[#F5A623]">{c.existing_domain}</span>
                  <span className="text-muted"> vs importé </span><span className="text-accent">{c.incoming_domain}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => resolveConflict(c.id, "keep_existing")} className="text-xs border border-border rounded px-2 py-1 text-muted hover:text-fg" data-testid={`conflict-keep-${c.id}`}>Conserver</button>
                  <button onClick={() => resolveConflict(c.id, "use_new")} className="text-xs border border-accent/40 rounded px-2 py-1 text-accent hover:bg-accent/10" data-testid={`conflict-use-${c.id}`}>Utiliser le nouveau</button>
                  <button onClick={() => resolveConflict(c.id, "mark_review")} className="text-xs border border-border rounded px-2 py-1 text-muted hover:text-fg">Marquer à vérifier</button>
                </div>
              </div>
            ))}
            {conflicts.reviews.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 bg-surface-2 border border-border rounded-md px-3 py-2 text-sm" data-testid={`review-${r.id}`}>
                <div className="min-w-0"><span className="text-fg font-medium">{r.source_name}</span>
                  <span className="text-muted"> → </span><span className="text-accent">{r.incoming_domain}</span>
                  <span className="text-muted text-xs"> · {r.candidates?.length || 0} candidat(s)</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + table */}
      <div className="surface rounded-lg p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-40 bg-surface-2 border-border" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="missing">Domaine manquant</SelectItem>
              <SelectItem value="ambiguous">À vérifier</SelectItem>
              <SelectItem value="probable">Probable</SelectItem>
              <SelectItem value="verified">Vérifié</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Pays" value={filters.country} onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))} className="w-28 bg-surface-2 border-border" data-testid="filter-country" />
          <Input placeholder="Région" value={filters.region} onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value }))} className="w-36 bg-surface-2 border-border" data-testid="filter-region" />
          <Input placeholder="Rechercher…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className="flex-1 min-w-[160px] bg-surface-2 border-border" data-testid="filter-q" />
          <button onClick={loadCompanies} className="text-muted hover:text-fg p-2" data-testid="refresh-companies"><ArrowClockwise size={16} /></button>
        </div>
        <div className="text-xs text-muted mb-2">{fmtNum(total)} société(s)</div>

        {/* Bulk action bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3 min-h-[36px]" data-testid="bulk-action-bar">
          <div className="text-xs text-muted">
            {selectedIds.size > 0 ? `${fmtNum(selectedIds.size)} société(s) sélectionnée(s)` : "Cochez des sociétés pour valider leur domaine en masse"}
          </div>
          <button onClick={bulkValidate} disabled={selectedIds.size === 0 || bulkValidating} data-testid="bulk-validate-btn"
            className="flex items-center gap-1.5 text-xs font-semibold bg-[#22C55E] text-black px-3 py-2 rounded-md hover-lift disabled:opacity-40 disabled:cursor-not-allowed">
            <CheckCircle size={14} weight="bold" />
            {bulkValidating ? "Validation…" : `Valider la sélection${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-muted text-xs border-b border-border">
              <th className="text-left py-2 font-medium w-8">
                <input type="checkbox" checked={pageAllSelected} onChange={toggleSelectAll} disabled={companies.length === 0}
                  className="accent-[#00E5FF] cursor-pointer" data-testid="select-all-domains" aria-label="Tout sélectionner" />
              </th>
              <th className="text-left py-2 font-medium">Société</th><th className="text-left font-medium">Ville</th>
              <th className="text-left font-medium">Domaine</th><th className="text-left font-medium">Statut</th>
              <th className="text-left font-medium">Source</th><th></th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-6 text-center text-muted animate-pulse">Chargement…</td></tr>
              ) : companies.length === 0 ? (
                <tr><td colSpan={7} className="py-6 text-center text-muted">Aucune société pour ce filtre.</td></tr>
              ) : companies.map((c) => {
                const sm = STATUS_META[c.domain_status] || STATUS_META.missing;
                const Icon = sm.icon;
                const canValidate = !!c.domain && (c.domain_status === "ambiguous" || c.domain_status === "probable");
                return (
                  <tr key={c.id} className="border-b border-border/40" data-testid={`domain-row-${c.id}`}>
                    <td className="py-2.5">
                      <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)}
                        className="accent-[#00E5FF] cursor-pointer" data-testid={`select-domain-${c.id}`} aria-label={`Sélectionner ${c.name}`} />
                    </td>
                    <td className="py-2.5 text-fg font-medium truncate max-w-[240px]">
                      {c.name}
                      {priority && c.signal_value > 0 && (
                        <span className="ml-2 text-[10px] text-accent bg-accent/10 border border-accent/30 rounded px-1.5 py-0.5 align-middle" data-testid={`value-${c.id}`}>valeur {c.signal_value}</span>
                      )}
                    </td>
                    <td className="text-muted">{c.city || "—"}</td>
                    <td>
                      {editing === c.id ? (
                        <div className="flex items-center gap-1">
                          <Input value={editVal} onChange={(e) => setEditVal(e.target.value)} placeholder="exemple.fr" className="h-7 w-40 bg-surface-2 border-border text-xs" data-testid={`edit-domain-input-${c.id}`} />
                          <button onClick={() => saveDomain(c)} className="text-accent p-1" data-testid={`save-domain-${c.id}`}><CheckCircle size={16} weight="fill" /></button>
                          <button onClick={() => { setEditing(null); setEditVal(""); }} className="text-muted p-1"><X size={14} /></button>
                        </div>
                      ) : c.domain ? <a href={`https://${c.domain}`} target="_blank" rel="noreferrer" className="text-accent hover:underline">{c.domain}</a> : <span className="text-muted">—</span>}
                    </td>
                    <td><span className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border" style={{ color: sm.color, borderColor: sm.color + "55", background: sm.color + "14" }}><Icon size={11} weight="fill" /> {sm.label}</span></td>
                    <td className="text-muted text-xs">{c.domain_source || "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      {editing !== c.id && (
                        <>
                          {canValidate && (
                            <button onClick={() => validateOne(c)} title="Valider ce domaine" className="text-muted hover:text-[#22C55E] p-1" data-testid={`validate-domain-${c.id}`}><Check size={15} weight="bold" /></button>
                          )}
                          <button onClick={() => { setEditing(c.id); setEditVal(c.domain || ""); }} className="text-muted hover:text-accent p-1" data-testid={`edit-domain-${c.id}`}><PencilSimple size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap mt-4 pt-3 border-t border-border" data-testid="pagination-footer">
            <div className="flex items-center gap-2 text-xs text-muted">
              <span>Par page</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="w-[72px] h-8 bg-surface-2 border-border" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted" data-testid="pagination-range">
                {fmtNum(page * pageSize + 1)}–{fmtNum(Math.min((page + 1) * pageSize, total))} sur {fmtNum(total)}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="page-prev"
                  className="flex items-center gap-1 text-xs border border-border rounded-md px-2.5 py-1.5 text-muted hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed">
                  <CaretLeft size={13} weight="bold" /> Précédent
                </button>
                <button onClick={() => setPage((p) => (((p + 1) * pageSize < total) ? p + 1 : p))} disabled={(page + 1) * pageSize >= total} data-testid="page-next"
                  className="flex items-center gap-1 text-xs border border-border rounded-md px-2.5 py-1.5 text-muted hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed">
                  Suivant <CaretRight size={13} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      {stats?.history?.length > 0 && (
        <div className="surface rounded-lg p-5 mt-6" data-testid="domain-history">
          <div className="flex items-center gap-2 mb-3"><ClockCounterClockwise size={16} className="text-accent" /><span className="font-display font-semibold text-fg">Historique import / export</span></div>
          <div className="space-y-1.5">
            {stats.history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 text-xs text-muted">
                <span className="font-mono">{h.at ? new Date(h.at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                <span className={h.type === "import" ? "text-accent" : "text-[#F5A623]"}>{h.type === "import" ? "Import" : "Export"}</span>
                <span className="text-fg">{h.label}</span>
                {h.type === "import" && h.summary ? (
                  <span>· {h.summary.enriched || 0} enrichies · {h.summary.created || 0} créées · {h.summary.conflicts || 0} conflits</span>
                ) : <span>· {fmtNum(h.count)} sociétés</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <DomainImportWizard open={showImport} onOpenChange={setShowImport} onDone={() => { loadStats(); loadConflicts(); loadCompanies(); }} />
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="surface rounded-lg p-4" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-2xl font-display font-bold" style={{ color: accent || "#F4F4F5" }}>{value}</div>
      <div className="text-xs text-muted mt-0.5">{label}</div>
    </div>
  );
}

function WorkflowStep({ n, title, desc, children }) {
  return (
    <div className="bg-surface-2/50 border border-border rounded-lg p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-6 h-6 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center text-accent text-xs font-bold">{n}</span>
        <span className="font-display font-semibold text-fg">{title}</span>
      </div>
      <p className="text-xs text-muted mb-3 flex-1">{desc}</p>
      {children}
    </div>
  );
}
